import React, { useState } from 'react';
import { IconButton, Tooltip, Snackbar } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';

/**
 * A button that copies the current page URL to clipboard.
 * Shows visual feedback when the link is copied.
 *
 * @param {Object} props
 * @param {string} [props.url] - Optional custom URL to copy. Defaults to current window location.
 * @param {string} [props.size] - Button size: 'small', 'medium', 'large'
 */
export default function CopyLinkButton({ url, size = 'medium' }) {
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopy = async () => {
    const linkToCopy = url || window.location.href.split('?')[0]; // Remove query params

    try {
      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);
      setSnackbarOpen(true);

      // Reset icon after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = linkToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);

      setCopied(true);
      setSnackbarOpen(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <IconButton
          onClick={handleCopy}
          size={size}
          sx={{
            color: copied ? 'success.main' : 'text.secondary',
            transition: 'color 0.2s',
            '&:hover': {
              backgroundColor: copied ? 'success.light' : 'action.hover',
            }
          }}
          aria-label="Copy link to clipboard"
        >
          {copied ? <CheckIcon /> : <LinkIcon />}
        </IconButton>
      </Tooltip>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
